import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import React from "react";
import { SubmitErrorHandler, useForm } from "react-hook-form";
import { z } from "zod";
import {
  BankAccountName,
  BankAccountType,
  CaregiverBankInformationWithEmail,
} from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { fmap } from "../../utils/fmap";
import { shakeElement } from "../../utils/shakeElement";
import AgreementText from "../AgreementText";
import ExternalLink from "../Auth/components/ExternalLink";
import { AppButton } from "../Generic/AppButton";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../Generic/AppCard";
import ControlledCheckbox from "../Generic/ControlledCheckbox";
import Flex from "../Generic/Flex";
import Margin from "../Margin";
import Question from "../OnboardingSection/Question";
import NotesText from "../Patient/components/NotesText";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";

const zFormSchema = z.object({
  accountType: z.string().min(1),
  bankId: z.string().min(1),
  routingNumber: z.string().length(9),
  accountNumber: z.string().min(1),
  email: z.string().email(),
  agreement: z.boolean().refine((value) => value),
});

type Props = {
  bankAccountNames: BankAccountName[];
  bankAccountTypes: BankAccountType[];
  defaultValues: CaregiverBankInformationWithEmail | null;
  title: string;
  onClickGoBack: () => void;
  onValidSubmit: (data: z.infer<typeof zFormSchema>) => void;
};

const FillBankInformationScreen = (props: Props) => {
  const agreementRef = React.useRef<HTMLDivElement>(null);
  const form = useForm<z.infer<typeof zFormSchema>>({
    resolver: zodResolver(zFormSchema),
    defaultValues: {
      accountNumber: props.defaultValues?.accountNumber ?? undefined,
      routingNumber: props.defaultValues?.routingNumber ?? undefined,
      accountType: fmap(props.defaultValues?.bankAccountTypeId, String) ?? undefined,
      bankId: fmap(props.defaultValues?.bankId, String) ?? undefined,
      email: props.defaultValues?.email ?? undefined,
      agreement: props.defaultValues?.hasAgreedToTerms ?? false,
    },
    mode: "onChange",
  });

  const handleInvalidSubmit: SubmitErrorHandler<z.infer<typeof zFormSchema>> = (errors) => {
    if (errors.agreement) {
      agreementRef.current?.scrollIntoView({ behavior: "smooth" });
      shakeElement(agreementRef);
    }
  };

  return (
    <Scaffold
      as="form"
      onSubmit={form.handleSubmit(props.onValidSubmit, handleInvalidSubmit)}
      title={<ScaffoldTitle label={translate(STRINGS.INSTAPAY)} />}
      footer={
        <ButtonRoot>
          <AppButton
            type="submit"
            color={form.formState.isValid ? "sexy" : "black"}
            block={true}
            className={classNames({ disabled: !form.formState.isValid })}
          >
            {translate(STRINGS.CONTINUE)}
          </AppButton>
        </ButtonRoot>
      }
      onClickGoBack={props.onClickGoBack}
    >
      <Margin m={16}>
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <div>{props.title}</div>
              <NotesText>{translate(STRINGS.CONFIRM_DEPOSIT_DETAILS)}</NotesText>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <Question.Group>
              <Question.Group>
                <Question.Label>{translate(STRINGS.ACCOUNT_TYPE)}</Question.Label>
                <Question.Select {...form.register("accountType")}>
                  <option value="null">{translate(STRINGS.SELECT)}</option>
                  {props.bankAccountTypes.map((accountType) => (
                    <Question.Select.Option key={`${accountType.id}`} value={`${accountType.id}`}>
                      {accountType.type}
                    </Question.Select.Option>
                  ))}
                </Question.Select>
                <Question.ErrorMessage>
                  {form.formState.errors.accountType?.message}
                </Question.ErrorMessage>
              </Question.Group>

              <Question.Group>
                <Question.Label>{translate(STRINGS.BANK_NAME)}</Question.Label>
                <Question.Select {...form.register("bankId")}>
                  <option value="null">{translate(STRINGS.SELECT)}</option>
                  {props.bankAccountNames.map((bank) => (
                    <Question.Select.Option key={`${bank.id}`} value={`${bank.id}`}>
                      {bank.name}
                    </Question.Select.Option>
                  ))}
                </Question.Select>
                <Question.ErrorMessage>
                  {form.formState.errors.bankId?.message}
                </Question.ErrorMessage>
              </Question.Group>

              <Question.Group>
                <Question.Label>{translate(STRINGS.ROUTING_NUMBER)}</Question.Label>
                <Question.Input {...form.register("routingNumber")} />
                <Question.ErrorMessage>
                  {form.formState.errors.routingNumber?.message}
                </Question.ErrorMessage>
              </Question.Group>

              <Question.Group>
                <Question.Label>{translate(STRINGS.ACCOUNT_NUMBER)}</Question.Label>
                <Question.Input {...form.register("accountNumber")} />
                <Question.ErrorMessage>
                  {form.formState.errors.accountNumber?.message}
                </Question.ErrorMessage>
              </Question.Group>

              <Question.Group>
                <Question.Label>{translate(STRINGS.EMAIL)}</Question.Label>
                <Question.Input {...form.register("email")} />
                <Question.ErrorMessage>
                  {form.formState.errors.email?.message}
                </Question.ErrorMessage>
              </Question.Group>

              <Flex gap={8} alignItems="center" ref={agreementRef}>
                <ControlledCheckbox
                  {...form.register("agreement")}
                  control={form.control}
                  aria-invalid={form.formState.errors.agreement !== undefined}
                />
                <AgreementText isInvalid={form.formState.errors.agreement !== undefined}>
                  <span>{translate(STRINGS.BY_ENROLLING_I_ACCEPT_THE)}</span>
                  <ExternalLink href="https://www.medflyt.com/terms-of-use">
                    {translate(STRINGS.TERMS_OF_USE)}
                  </ExternalLink>
                  <span>{translate(STRINGS.AND_AMPERSAND)}</span>
                  <ExternalLink href="https://www.medflyt.com/privacy-policy">
                    {translate(STRINGS.PRIVACY_POLICY)}
                  </ExternalLink>
                </AgreementText>
              </Flex>
            </Question.Group>
          </AppCardContent>
        </AppCard>
      </Margin>
    </Scaffold>
  );
};

const ButtonRoot = styled.div`
  padding: 16px;
`;

export default FillBankInformationScreen;
